import React from "react"
import { Link } from "gatsby"
import * as buttonStyles from "../../ds/button.module.css"
import * as gradientStyles from "../../ds/gradients.module.css"
import PremagicIcon from "../../images/logo/144x144.png"
import CventLogo from "../../images/integrations/cvent.png"
import HubspotLogo from "../../images/integrations/hubspot.png"
import SwapcardLogo from "../../images/integrations/swapcard.png"
import LumaLogo from "../../images/integrations/luma.png"
import GatsbyLogo from "../../images/integrations/gatsby.png"
import EventifyLogo from "../../images/integrations/eventify.png"

type Platform = {
  name: string
  slug?: string
  logo?: string
  url: string
}

const SUPPORTED_PLATFORMS: Platform[] = [
  { name: "Swapcard", slug: "swapcard", logo: SwapcardLogo, url: "https://www.swapcard.com/" },
  { name: "Luma", slug: "luma", logo: LumaLogo, url: "https://lu.ma/" },
  { name: "Cvent", slug: "cvent", logo: CventLogo, url: "https://www.cvent.com/" },
  { name: "Gatsby", slug: "gatsby-events", logo: GatsbyLogo, url: "https://www.gatsby.events/" },
  { name: "HubSpot", slug: "hubspot", logo: HubspotLogo, url: "https://www.hubspot.com/" },
  { name: "Eventify", logo: EventifyLogo, url: "https://eventify.io/" },
  { name: "Swoogo", url: "https://www.swoogo.com/" },
]

const SectionHeading: React.FC<{
  eyebrow?: string
  title: string
  description?: string
  align?: "left" | "center"
}> = ({ eyebrow, title, description, align = "center" }) => (
  <div
    className={`max-w-3xl mb-12 ${align === "center" ? "text-center mx-auto" : "text-left"}`}
  >
    {eyebrow && (
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7253F5] mb-3">
        {eyebrow}
      </p>
    )}
    <h2 className="text-3xl md:text-4xl font-bold text-[#1F0F61] leading-tight">
      {title}
    </h2>
    {description && (
      <p className="mt-4 text-base md:text-lg text-gray-600 leading-relaxed">
        {description}
      </p>
    )}
  </div>
)

export const HowItWorksHero: React.FC = () => (
  <section
    className={`relative overflow-hidden pt-24 pb-16 md:pt-32 md:pb-20 ${gradientStyles["gradient-light-pink"]}`}
  >
    <div className="pm-container relative z-10">
      <nav className="mb-8 text-sm text-gray-500" aria-label="Breadcrumb">
        <Link to="/integrations/" className="hover:text-[#2b4bc3] transition-colors">
          Integrations
        </Link>
        <span className="mx-2 text-gray-300">/</span>
        <span className="text-gray-700 font-medium">How it works</span>
      </nav>

      <div className="max-w-4xl">
        <span className="inline-block rounded-full bg-white/80 backdrop-blur px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#7253F5] ring-1 ring-purple-100 mb-6">
          Customer Integration Guide
        </span>
        <h1 className="text-4xl md:text-6xl font-bold text-[#1F0F61] leading-[1.05] tracking-tight">
          How Premagic{" "}
          <span className={gradientStyles["gradient-text"]}>integrates</span>{" "}
          with your event platform
        </h1>
        <p className="mt-6 text-lg md:text-xl text-gray-600 max-w-2xl leading-relaxed">
          A plain-English overview of what gets synced, what your attendees see,
          and what you can embed where, so you can plan your rollout in an
          afternoon.
        </p>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/request-a-demo/"
            className={`${buttonStyles.btn} ${buttonStyles["btn-primary"]}`}
          >
            Talk to our integrations team
          </Link>
          <a
            href="#data-integration"
            className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
          >
            Jump to setup
          </a>
        </div>
      </div>
    </div>

    <div className="absolute -left-20 top-20 h-64 w-64 rounded-full bg-pink-200 opacity-20 blur-3xl" />
    <div className="absolute -right-20 bottom-20 h-64 w-64 rounded-full bg-blue-200 opacity-20 blur-3xl" />
  </section>
)

export const TableOfContents: React.FC = () => {
  const items = [
    { href: "#what-premagic-does", label: "What Premagic does" },
    { href: "#two-halves", label: "The two halves of an integration" },
    { href: "#journey", label: "The end-to-end journey" },
    { href: "#data-integration", label: "Getting your data in" },
    { href: "#widgets", label: "Widgets to embed" },
    { href: "#email", label: "Email from your domain" },
    { href: "#success", label: "What success looks like" },
  ]

  return (
    <section className="border-y border-gray-100 bg-white py-6">
      <div className="pm-container">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span className="font-semibold text-gray-400 uppercase tracking-wider text-xs">
            On this page
          </span>
          {items.map(item => (
            <a
              key={item.href}
              href={item.href}
              className="text-gray-600 hover:text-[#2b4bc3] transition-colors"
            >
              {item.label}
            </a>
          ))}
        </div>
      </div>
    </section>
  )
}

export const WhatPremagicDoes: React.FC = () => {
  const features = [
    {
      title: "Advocacy campaigns",
      description:
        "Attendees, speakers, sponsors and exhibitors are invited to post about your event on social media using pre-designed, branded, personalised posters and one-click share flows.",
    },
    {
      title: "AI photo delivery",
      description:
        "During and after your event, attendees receive their own photos on their phone, identified by face recognition. No app install. Each attendee gets a personal gallery containing only photos they appear in.",
    },
  ]

  return (
    <section id="what-premagic-does" className="py-20 md:py-28 bg-white">
      <div className="pm-container">
        <SectionHeading
          eyebrow="What Premagic does"
          title="An event marketing platform that turns attendees into amplifiers"
          description="Premagic packages content so it's easy for your attendees to share, using event photos, digital posters and AI avatars to make every post feel personal."
        />

        <div className="grid gap-6 md:grid-cols-2 max-w-5xl mx-auto">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-gray-50 p-8 transition-all hover:shadow-md hover:border-purple-100"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1F0F61] text-white text-sm font-bold">
                  {i + 1}
                </span>
                <h3 className="text-xl font-bold text-[#1F0F61]">{f.title}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">{f.description}</p>
            </div>
          ))}
        </div>

        <p className="mt-10 mx-auto max-w-3xl text-center text-gray-600">
          Both features need your attendee list, ideally enriched with role,
          headshot, company and contact details. Your event platform owns that
          list. The integration brings the two sides together.
        </p>
      </div>
    </section>
  )
}

export const TwoHalves: React.FC = () => {
  const halves = [
    {
      eyebrow: "Half 1",
      title: "Data integration",
      description:
        "Get your events and registrations into Premagic so we know who to send photos to and who to recruit for advocacy. Push gallery and poster links back so they live on the attendee's record in your platform.",
      bullets: [
        "Import existing registrations",
        "Live updates via webhooks",
        "Write magic links back as custom fields",
      ],
    },
    {
      eyebrow: "Half 2",
      title: "Widget / iframe integration",
      description:
        "Show Premagic surfaces (personal photo gallery, share posters, live event slider) inside your event platform's pages so your attendees never leave it to use Premagic.",
      bullets: [
        "Quick Register, Autofill, Poster Creator",
        "“My photos” iframe",
        "Live photo slider for landing pages",
      ],
    },
  ]

  return (
    <section id="two-halves" className="py-20 md:py-28 bg-gray-50">
      <div className="pm-container">
        <SectionHeading
          eyebrow="The shape of an integration"
          title="Two halves. Ship one or both."
          description="The most valuable setups do both, but you can start with whichever half maps to your stack today."
        />

        <div className="grid gap-6 md:grid-cols-2 max-w-5xl mx-auto">
          {halves.map(h => (
            <div
              key={h.title}
              className="rounded-2xl bg-white p-8 border border-gray-100 hover:shadow-md transition-all"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF6550] mb-2">
                {h.eyebrow}
              </p>
              <h3 className="text-2xl font-bold text-[#1F0F61] mb-3">
                {h.title}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                {h.description}
              </p>
              <ul className="space-y-2">
                {h.bullets.map(b => (
                  <li
                    key={b}
                    className="flex items-start gap-2 text-sm text-gray-700"
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#7253F5] flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export const EndToEndJourney: React.FC = () => {
  const steps = [
    {
      title: "Connect your platform",
      description:
        "From inside the Premagic dashboard, link your event platform with a few clicks.",
    },
    {
      title: "Premagic backfills your registrations",
      description:
        "Your existing registration list is imported in one pass. Connect mid-event and we'll backfill everything that came in before.",
    },
    {
      title: "Live updates flow through",
      description:
        "Webhooks subscribe Premagic to new registrations, edits and cancellations. They reach us within seconds.",
    },
    {
      title: "Create campaigns by audience slice",
      description:
        "Photo delivery, advocacy posters, speaker amplification. Activate against any segment: all attendees, speakers only, sponsors only.",
    },
    {
      title: "Premagic writes back to your platform",
      description:
        "Each attendee's personal gallery link, advocacy poster link and face ID are saved as custom fields on their record inside your platform.",
    },
    {
      title: "Attendees see Premagic surfaces inline",
      description:
        "A “My photos” iframe inside your app, a share-this-event poster on the registration success page, a live gallery on the event landing page.",
    },
    {
      title: "Optional: email from your domain",
      description:
        "Premagic outbound mail can be sent from your own domain so all event communications carry one brand.",
    },
  ]

  return (
    <section id="journey" className="py-20 md:py-28 bg-white">
      <div className="pm-container">
        <SectionHeading
          eyebrow="The full picture"
          title="The end-to-end journey, step by step"
        />

        <ol className="relative max-w-3xl mx-auto">
          <span
            aria-hidden
            className="absolute left-4 top-2 bottom-2 w-px bg-gradient-to-b from-[#7253F5] via-[#C466FF] to-[#FF6550] opacity-40"
          />
          {steps.map((step, i) => (
            <li key={step.title} className="relative pl-14 pb-10 last:pb-0">
              <span className="absolute left-0 top-0 flex h-9 w-9 items-center justify-center rounded-full bg-white ring-2 ring-[#7253F5] text-[#1F0F61] font-bold text-sm shadow-sm">
                {i + 1}
              </span>
              <h3 className="text-lg font-semibold text-[#1F0F61] mb-1">
                {step.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">{step.description}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

export const DataIntegration: React.FC = () => {
  return (
    <section id="data-integration" className="py-20 md:py-28 bg-gray-50">
      <div className="pm-container">
        <SectionHeading
          eyebrow="Half 1 · In detail"
          title="Getting your registration data into Premagic"
          description="Two paths. Direct API is the better experience. CSV works with any platform."
        />

        {/* Direct API */}
        <div className="max-w-5xl mx-auto rounded-2xl bg-white border border-gray-100 p-8 md:p-10 mb-6">
          <div className="flex items-baseline gap-3 mb-2">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#7253F5]">
              Recommended
            </span>
            <span className="text-xs text-gray-400">· best experience</span>
          </div>
          <h3 className="text-2xl font-bold text-[#1F0F61] mb-3">
            Direct API integration
          </h3>
          <p className="text-gray-600 leading-relaxed mb-8 max-w-3xl">
            Connect from inside the Premagic dashboard in a few clicks. We list
            your events, let you pick segments and custom attributes to sync,
            wire in webhooks for continuous flow, and backfill any existing
            registrations.
          </p>

          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">
            Direct integrations available today
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
            {SUPPORTED_PLATFORMS.map(p => {
              const inner = (
                <div className="flex h-20 flex-col items-center justify-center gap-1 rounded-xl border border-gray-100 bg-white px-3 py-2 transition-all hover:border-purple-200 hover:shadow-sm">
                  {p.logo ? (
                    <img
                      src={p.logo}
                      alt={`${p.name} logo`}
                      className="h-8 max-w-full object-contain"
                    />
                  ) : (
                    <span className="text-base font-bold text-[#1F0F61]">
                      {p.name}
                    </span>
                  )}
                  {p.logo && (
                    <span className="text-xs text-gray-500">{p.name}</span>
                  )}
                </div>
              )
              return p.slug ? (
                <Link
                  key={p.name}
                  to={`/integrations/${p.slug}/`}
                  className="block"
                  aria-label={`${p.name} integration`}
                >
                  {inner}
                </Link>
              ) : (
                <div key={p.name}>{inner}</div>
              )
            })}
          </div>

          <p className="text-sm text-gray-500">
            Don't see your platform?{" "}
            <Link to="/contact-us/" className="text-[#2b4bc3] hover:underline">
              Contact us
            </Link>
            . We add new platforms regularly and can usually scope a new
            integration quickly if there's a clean API on the other side.
          </p>
        </div>

        {/* CSV */}
        <div className="max-w-5xl mx-auto rounded-2xl bg-white border border-gray-100 p-8 md:p-10">
          <div className="flex items-baseline gap-3 mb-2">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF6550]">
              Universal fallback
            </span>
            <span className="text-xs text-gray-400">· works with any platform</span>
          </div>
          <h3 className="text-2xl font-bold text-[#1F0F61] mb-3">
            CSV import &amp; export
          </h3>
          <p className="text-gray-600 leading-relaxed mb-6 max-w-3xl">
            If your platform isn't supported via API yet, or you prefer a manual
            loop, Premagic supports CSV in both directions.
          </p>

          <div className="grid gap-4 md:grid-cols-3">
            {[
              {
                title: "Import",
                body:
                  "Export your registration list as a CSV and upload it inside Premagic. We'll guide you through mapping columns to Premagic fields.",
              },
              {
                title: "Re-imports",
                body:
                  "Re-export the CSV and upload it again. Premagic de-duplicates by email, so existing attendees aren't touched and only new rows get added.",
              },
              {
                title: "Export",
                body:
                  "Download a CSV that includes each attendee's gallery link, advocacy poster link and face ID. Drop it back into your platform.",
              },
            ].map(item => (
              <div
                key={item.title}
                className="rounded-xl bg-gray-50 p-5 border border-gray-100"
              >
                <h4 className="text-base font-bold text-[#1F0F61] mb-2">
                  {item.title}
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.body}
                </p>
              </div>
            ))}
          </div>

          <p className="mt-6 text-sm text-gray-500 italic">
            CSV works, but if registrations roll in during the days before, or
            you have live walk-ins, the direct API integration is a much better
            experience for both you and your attendees.
          </p>
        </div>
      </div>
    </section>
  )
}

const WidgetCard: React.FC<{
  badge: string
  title: string
  description: string
  placements: string[]
}> = ({ badge, title, description, placements }) => (
  <div className="rounded-2xl bg-white border border-gray-100 p-7 hover:shadow-md hover:border-purple-100 transition-all h-full">
    <span className="inline-block rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#7253F5] mb-4">
      {badge}
    </span>
    <h4 className="text-lg font-bold text-[#1F0F61] mb-2">{title}</h4>
    <p className="text-sm text-gray-600 leading-relaxed mb-5">{description}</p>
    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
      Where it should go
    </p>
    <ul className="space-y-1.5">
      {placements.map(p => (
        <li key={p} className="flex items-start gap-2 text-sm text-gray-700">
          <span className="mt-1.5 h-1 w-1 rounded-full bg-[#FF6550] flex-shrink-0" />
          <span>{p}</span>
        </li>
      ))}
    </ul>
  </div>
)

export const Widgets: React.FC = () => {
  const registrationWidgets = [
    {
      badge: "Quick Register",
      title: "One-tap “Register with LinkedIn”",
      description:
        "Attendee clicks one button, signs in to LinkedIn in a popup, and Premagic fills in name, email, headshot, company and role automatically, then submits the registration. They never type into a form.",
      placements: [
        "On your event's public landing page",
        "Top of your registration page",
        "In your event mobile app or PWA",
      ],
    },
    {
      badge: "LinkedIn Autofill",
      title: "Keep your form, drop the typing",
      description:
        "After Quick Register, this autofills the registration form with available information. Use when you want to keep validation rules, custom questions and payment integration as-is.",
      placements: [
        "On your existing registration form page",
        "When form is on a different page from the landing page",
      ],
    },
    {
      badge: "Poster Creator",
      title: "Share-on-confirmation moment",
      description:
        "Embed a personalised “I'm attending” poster (“Asha is attending DesignCon 2026”) on the thank-you page. Drop straight to LinkedIn, X, Instagram or WhatsApp in two taps. Intent to share peaks the moment after commitment.",
      placements: [
        "Thank-you page after registration (highest value)",
        "Speaker, exhibitor and sponsor confirmation flows",
        "The ticket page (recurring share prompt)",
        "Linked from your order-confirmation email",
      ],
    },
  ]

  return (
    <section id="widgets" className="py-20 md:py-28 bg-white">
      <div className="pm-container">
        <SectionHeading
          eyebrow="Half 2 · In detail"
          title="Widgets to embed in your event platform"
          description="Plain HTML / JavaScript snippets, generated in your Premagic dashboard and pasted into your platform pages. No npm package, no framework dependency."
        />

        {/* Registration flow widgets */}
        <div className="max-w-6xl mx-auto mb-16">
          <div className="flex items-center gap-3 mb-8">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1F0F61] text-white font-bold text-sm">
              1
            </span>
            <h3 className="text-xl md:text-2xl font-bold text-[#1F0F61]">
              In the registration &amp; ticket-purchase flow
            </h3>
          </div>
          <p className="text-gray-600 mb-8 max-w-3xl">
            The highest-value half of the widget integration. Every prospective
            attendee passes through this flow, and with the right widgets, each
            registration generates one social share and pre-fills its own form.
          </p>

          <div className="grid gap-5 md:grid-cols-3">
            {registrationWidgets.map(w => (
              <WidgetCard key={w.title} {...w} />
            ))}
          </div>
        </div>

        {/* My Photos iframe */}
        <div className="max-w-6xl mx-auto mb-16">
          <div className="flex items-center gap-3 mb-8">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1F0F61] text-white font-bold text-sm">
              2
            </span>
            <h3 className="text-xl md:text-2xl font-bold text-[#1F0F61]">
              The “My photos” iframe
            </h3>
          </div>

          <div className="rounded-2xl bg-gradient-to-br from-purple-50 via-white to-pink-50 border border-purple-100 p-8 md:p-10">
            <p className="text-gray-700 leading-relaxed mb-6 max-w-3xl">
              Once Premagic has matched photos to an attendee, a{" "}
              <code className="rounded bg-white px-1.5 py-0.5 text-xs font-mono text-[#1F0F61] border border-gray-200">
                PREMAGIC_PERSONAL_GALLERY
              </code>{" "}
              magic link is populated on the attendee's record. Your platform
              reads that field and shows a “My photos” button when it's
              populated, opening the link inside an iframe (modal, drawer or
              full-page route, your call).
            </p>

            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <h4 className="text-sm font-bold text-[#1F0F61] mb-3 uppercase tracking-wider">
                  Live updates inside the iframe
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Once the iframe is open, it's reactive. As the photographer
                  uploads more photos and the pipeline matches new ones to this
                  attendee's face, those photos appear inside the same iframe
                  with no refresh. You set the <code className="text-xs">src</code>{" "}
                  once and walk away.
                </p>
              </div>
              <div>
                <h4 className="text-sm font-bold text-[#1F0F61] mb-3 uppercase tracking-wider">
                  Email reuse
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  The same magic link drops cleanly into your post-event
                  transactional email as a “View your photos” button. Zero
                  additional work.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Live photo slider */}
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1F0F61] text-white font-bold text-sm">
              3
            </span>
            <h3 className="text-xl md:text-2xl font-bold text-[#1F0F61]">
              Live photo slider on the event landing page
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-gray-600 leading-relaxed mb-6">
                A live, rotating gallery of approved event photos that you embed
                on your event landing or detail page. Visitors who land there
                during or after the event see the event{" "}
                <em className="text-[#1F0F61] not-italic font-semibold">
                  happening
                </em>
                . A continuously-updating proof point that converts hesitant
                prospects into registrations for the next edition.
              </p>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                Where it should go
              </p>
              <ul className="space-y-1.5">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="mt-1.5 h-1 w-1 rounded-full bg-[#FF6550] flex-shrink-0" />
                  <span>
                    On your event's public landing page, just below the hero or
                    in a “Highlights” band
                  </span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="mt-1.5 h-1 w-1 rounded-full bg-[#FF6550] flex-shrink-0" />
                  <span>
                    In your post-event recap email as a teaser linking back to
                    the landing page
                  </span>
                </li>
              </ul>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-[#1F0F61] to-[#7253F5] p-1">
              <div className="rounded-xl bg-white p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
                  How you embed it
                </p>
                <p className="text-gray-700 leading-relaxed text-sm">
                  A single async{" "}
                  <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-mono text-[#1F0F61]">
                    &lt;script&gt;
                  </code>{" "}
                  snippet generated in your Premagic dashboard. The slider
                  self-updates as new photos are approved during the event.
                </p>
                <p className="mt-4 text-gray-700 leading-relaxed text-sm">
                  Paste the embed once and walk away.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export const EmailFromYourDomain: React.FC = () => {
  const steps = [
    "Contact Premagic support and ask for outbound email to be served from your domain.",
    "Support replies with the DNS records to add (an SPF entry, two or three DKIM CNAME records, and a recommended DMARC policy).",
    "You add the records at your DNS provider and notify support.",
    "Premagic verifies and flips your account. From that point, every Premagic email for that account is sent from your domain.",
  ]

  return (
    <section id="email" className="py-20 md:py-28 bg-gray-50">
      <div className="pm-container">
        <SectionHeading
          eyebrow="Optional"
          title="Email from your own domain"
          description="By default, Premagic outbound mail is sent from a Premagic-owned domain. With strict brand requirements, have it sent from your domain so all event communications share one sender identity and one deliverability reputation."
        />

        <ol className="max-w-3xl mx-auto space-y-3">
          {steps.map((step, i) => (
            <li
              key={i}
              className="flex items-start gap-4 rounded-xl bg-white border border-gray-100 p-5"
            >
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#7253F5]/10 text-[#7253F5] font-bold text-sm">
                {i + 1}
              </span>
              <p className="text-gray-700 leading-relaxed pt-1">{step}</p>
            </li>
          ))}
        </ol>

        <p className="mt-8 text-center text-sm text-gray-500">
          No engineering work needed on your side beyond adding the DNS records.
        </p>
      </div>
    </section>
  )
}

export const SuccessOutcomes: React.FC = () => {
  const outcomes = [
    "Every registration on your event platform appearing inside Premagic within seconds, with role, headshot, company and contact details mapped.",
    "A noticeable spike in social shares from the registration-success page (“I'm attending X”), each one a branded acquisition channel for your next edition.",
    "A personal gallery link landing on every attendee's record on your event platform once their photos are ready.",
    "Attendees opening “My photos” from inside your ticket page or mobile app and seeing only their own photos, updating live during the event.",
  ]

  return (
    <section id="success" className="py-20 md:py-28 bg-white">
      <div className="pm-container">
        <SectionHeading
          eyebrow="The outcome"
          title="What success looks like"
          description="A fully-deployed Premagic setup produces these four signals."
        />

        <div className="grid gap-5 md:grid-cols-2 max-w-5xl mx-auto">
          {outcomes.map((o, i) => (
            <div
              key={i}
              className="flex items-start gap-4 rounded-2xl bg-gradient-to-br from-white to-pink-50 border border-pink-100 p-6"
            >
              <div className="flex-shrink-0 mt-1">
                <svg
                  className="h-6 w-6 text-[#FF6550]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <p className="text-gray-700 leading-relaxed">{o}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export const HowItWorksCTA: React.FC = () => (
  <section className="py-20 md:py-24 bg-[#1F0F61] text-white text-center relative overflow-hidden">
    <div
      aria-hidden
      className="absolute -top-40 left-1/2 -translate-x-1/2 h-96 w-96 rounded-full bg-[#7253F5] opacity-20 blur-3xl"
    />
    <div className="absolute -bottom-32 right-10 h-72 w-72 rounded-full bg-[#FF6550] opacity-10 blur-3xl" />

    <div className="pm-container relative z-10">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white leading-tight">
          Ready to plan your integration?
        </h2>
        <p className="text-base md:text-lg text-purple-100 mb-10 leading-relaxed">
          Talk to our integrations team. We'll walk through your event platform,
          pick the right half (or both) and have a working setup ready before
          your next event.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/request-a-demo/"
            className={`${buttonStyles.btn} bg-white text-[#1F0F61] hover:bg-gray-100`}
          >
            Talk to our team
          </Link>
          <Link
            to="/integrations/"
            className={`${buttonStyles.btn} border border-white/40 text-white hover:bg-white/10`}
          >
            Browse all integrations
          </Link>
        </div>
      </div>
    </div>
  </section>
)
